import { useEffect, useState } from "react";
import type { RefObject } from "react";

/**
 * Measures how much vertical room an AntD <Table> body has inside a
 * height-constrained container, so the table can scroll internally instead of
 * pushing the whole page past the fold.
 *
 * Pass the ref of a `flex: 1; min-height: 0` wrapper that holds the Table. The
 * returned value goes straight into `scroll={{ y }}`, which pins the column
 * header and keeps the pagination row on screen.
 *
 * The header and pagination are measured rather than hardcoded — their heights
 * shift with font size, browser zoom, and whether the pager is rendered at all.
 */
export function useTableScrollY(
  ref: RefObject<HTMLDivElement | null>,
  deps: unknown[] = [],
): number | undefined {
  const [y, setY] = useState<number>();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      const head = el.querySelector<HTMLElement>(".ant-table-thead");
      const pager = el.querySelector<HTMLElement>(".ant-table-pagination");
      // AntD gives the pagination row a 16px vertical margin.
      const next =
        el.clientHeight -
        (head?.offsetHeight ?? 0) -
        (pager ? pager.offsetHeight + 16 : 0);

      // Never collapse to nothing on very short windows — let the page scroll
      // a little rather than render a table with no visible rows.
      const clamped = Math.max(next, 160);
      setY((prev) => (prev === clamped ? prev : clamped));
    };

    measure();

    // clientHeight is fixed by the flex parent, so setting `y` cannot feed back
    // into this observer and cause a resize loop.
    const ro = new ResizeObserver(measure);
    ro.observe(el);

    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return y;
}
